import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import {
  Shield,
  Lock,
  Eye,
  EyeOff,
  Server,
  Database,
  Key,
  CheckCircle,
  XCircle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  FileText,
  Cpu,
  Network,
  Award,
  AlertTriangle,
  Zap
} from 'lucide-react'
import LandingHeader from '../components/LandingHeader'

// FAQ Accordion Component
function FAQItem({ question, answer, isOpen, onClick }) {
  return (
    <div className="border-b border-gray-200">
      <button
        className="w-full py-6 flex items-center justify-between text-left"
        onClick={onClick}
      >
        <span className="text-lg font-medium text-gray-900">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-purple-600" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="pb-6"
        >
          <p className="text-gray-600 leading-relaxed">{answer}</p>
        </motion.div>
      )}
    </div>
  )
}

// Comparison Table Row
function ComparisonRow({ feature, traditional, fhe }) {
  return (
    <tr className="border-b border-gray-100">
      <td className="py-4 px-4 font-medium text-gray-900">{feature}</td>
      <td className="py-4 px-4 text-center">
        {traditional ? (
          <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
        ) : (
          <XCircle className="w-5 h-5 text-red-500 mx-auto" />
        )}
      </td>
      <td className="py-4 px-4 text-center">
        {fhe ? (
          <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
        ) : (
          <XCircle className="w-5 h-5 text-red-500 mx-auto" />
        )}
      </td>
    </tr>
  )
}

export default function PrivacyTech() {
  const { t } = useTranslation()
  const [openFAQ, setOpenFAQ] = useState(0)

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  }

  return (
    <div className="min-h-screen bg-white">
      <LandingHeader />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-purple-900 via-indigo-900 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-purple-500/20 border border-purple-400/30 rounded-full px-4 py-2 mb-6">
              <Shield className="w-4 h-4 text-purple-300" />
              <span className="text-purple-200 text-sm font-medium">
                {t('privacyTech.badge', 'Documentacion Tecnica')}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('privacyTech.hero.title', 'Por que FHE no viola la privacidad')}
            </h1>
            <p className="text-xl text-purple-100 max-w-3xl mx-auto mb-8">
              {t('privacyTech.hero.subtitle', 'Explicacion tecnica de como Fully Homomorphic Encryption permite hacer Machine Learning sobre datos sensibles sin jamas exponer la informacion original.')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
                <Lock className="w-5 h-5 text-green-400" />
                <span className="text-white">128-bit Security</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
                <Award className="w-5 h-5 text-blue-400" />
                <span className="text-white">Post-Quantum Safe</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
                <CheckCircle className="w-5 h-5 text-purple-400" />
                <span className="text-white">Zero-Knowledge</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What is FHE Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeIn} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('privacyTech.whatIsFHE.title', 'Que es la Encriptacion Homomorfica?')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('privacyTech.whatIsFHE.subtitle', 'Una tecnologia revolucionaria que permite computar sobre datos encriptados sin descifrarlos.')}
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Analogy Box */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Key className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">
                  {t('privacyTech.whatIsFHE.analogy.title', 'Analogia: La Caja Fuerte Magica')}
                </h3>
              </div>
              <div className="space-y-4 text-gray-600">
                <p>
                  {t('privacyTech.whatIsFHE.analogy.p1', 'Imagina una caja fuerte con guantes incorporados:')}
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('privacyTech.whatIsFHE.analogy.point1', 'Puedes meter las manos a traves de los guantes')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('privacyTech.whatIsFHE.analogy.point2', 'Puedes manipular lo que hay dentro')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <span>{t('privacyTech.whatIsFHE.analogy.point3', 'Pero NO puedes ver lo que estas manipulando')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Lock className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                    <span>{t('privacyTech.whatIsFHE.analogy.point4', 'Solo el dueno de la llave puede abrir y ver el resultado')}</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Technical Definition */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="bg-gradient-to-br from-purple-600 to-indigo-600 rounded-2xl p-8 text-white">
                <h3 className="text-xl font-bold mb-4">
                  {t('privacyTech.whatIsFHE.definition.title', 'Definicion Formal')}
                </h3>
                <div className="bg-white/10 rounded-xl p-4 font-mono text-sm">
                  <p>D(E(a) ⊕ E(b)) = a ○ b</p>
                </div>
                <p className="mt-4 text-purple-100">
                  {t('privacyTech.whatIsFHE.definition.explanation', 'Donde E es encriptar, D es desencriptar. Las operaciones sobre datos encriptados producen resultados que, al desencriptarse, son equivalentes a operar sobre los datos originales.')}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                  <div className="text-3xl font-bold text-purple-600 mb-2">2009</div>
                  <p className="text-sm text-gray-600">
                    {t('privacyTech.whatIsFHE.facts.year', 'Craig Gentry inventa FHE (IBM/Stanford)')}
                  </p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                  <div className="text-3xl font-bold text-purple-600 mb-2">2017</div>
                  <p className="text-sm text-gray-600">
                    {t('privacyTech.whatIsFHE.facts.ckks', 'CKKS permite ML sobre datos encriptados')}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How Privacy is Preserved Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeIn} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('privacyTech.howItWorks.title', 'Como se Preserva la Privacidad')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('privacyTech.howItWorks.subtitle', 'El servidor NUNCA ve tus datos. Solo procesa informacion encriptada que para el es indistinguible de ruido aleatorio.')}
            </p>
          </motion.div>

          {/* Flow Diagram */}
          <div className="bg-gray-50 rounded-3xl p-8 md:p-12 mb-16">
            <div className="grid md:grid-cols-3 gap-8">
              {/* Client Side - Encrypt */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="bg-green-100 rounded-2xl p-6 mb-4">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Database className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">1. {t('privacyTech.howItWorks.step1.title', 'Tu Dispositivo')}</h3>
                  <p className="text-sm text-gray-600">
                    {t('privacyTech.howItWorks.step1.description', 'Tus datos originales se encriptan ANTES de salir de tu dispositivo. Solo tu tienes la clave privada.')}
                  </p>
                </div>
                <div className="flex items-center justify-center gap-2 text-green-600">
                  <Eye className="w-5 h-5" />
                  <span className="font-medium">{t('privacyTech.howItWorks.step1.visibility', 'Tu VES los datos')}</span>
                </div>
              </motion.div>

              {/* Arrow */}
              <div className="hidden md:flex items-center justify-center">
                <div className="flex flex-col items-center">
                  <ArrowRight className="w-8 h-8 text-purple-400" />
                  <div className="bg-purple-100 rounded-lg px-3 py-1 mt-2">
                    <span className="text-xs font-mono text-purple-600">E(datos) = 🔒</span>
                  </div>
                </div>
              </div>

              {/* Server Side - Process */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="text-center"
              >
                <div className="bg-red-50 rounded-2xl p-6 mb-4 border-2 border-dashed border-red-200">
                  <div className="w-16 h-16 bg-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Server className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">2. {t('privacyTech.howItWorks.step2.title', 'Servidor Xcapit')}</h3>
                  <p className="text-sm text-gray-600">
                    {t('privacyTech.howItWorks.step2.description', 'El servidor procesa datos ENCRIPTADOS. Ejecuta el modelo ML sin poder ver ni un solo valor real.')}
                  </p>
                </div>
                <div className="flex items-center justify-center gap-2 text-red-600">
                  <EyeOff className="w-5 h-5" />
                  <span className="font-medium">{t('privacyTech.howItWorks.step2.visibility', 'Servidor NO VE nada')}</span>
                </div>
              </motion.div>
            </div>

            {/* What Server Sees */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="mt-12 bg-slate-900 rounded-2xl p-6 overflow-hidden"
            >
              <div className="flex items-center gap-3 mb-4">
                <Server className="w-5 h-5 text-slate-400" />
                <span className="text-slate-300 font-medium">{t('privacyTech.howItWorks.serverView.title', 'Lo que ve el servidor:')}</span>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs text-slate-500 mb-2">{t('privacyTech.howItWorks.serverView.original', 'Tus datos originales:')}</p>
                  <div className="bg-slate-800 rounded-lg p-4 font-mono text-green-400 text-sm">
                    <p>salario: $85,000</p>
                    <p>edad: 34</p>
                    <p>historial_credito: 720</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-2">{t('privacyTech.howItWorks.serverView.encrypted', 'Lo que recibe el servidor (encriptado):')}</p>
                  <div className="bg-slate-800 rounded-lg p-4 font-mono text-red-400 text-sm overflow-hidden">
                    <p className="truncate">0x7a3f8c2d4b6e1a9f...</p>
                    <p className="truncate">0x9c2e5f8a1b4d7e3c...</p>
                    <p className="truncate">0x3d6a9c2f5e8b1a4d...</p>
                  </div>
                </div>
              </div>
              <p className="mt-4 text-slate-400 text-sm">
                {t('privacyTech.howItWorks.serverView.explanation', 'Para el servidor, estos datos encriptados son matematicamente indistinguibles de ruido aleatorio. No existe forma de obtener los valores originales sin la clave privada.')}
              </p>
            </motion.div>
          </div>

          {/* 5 Guarantees */}
          <div className="grid md:grid-cols-5 gap-6">
            {[
              { icon: Lock, title: t('privacyTech.guarantees.g1.title', 'Encriptacion E2E'), description: t('privacyTech.guarantees.g1.desc', 'Datos encriptados antes de salir de tu dispositivo'), color: 'purple' },
              { icon: EyeOff, title: t('privacyTech.guarantees.g2.title', 'Zero Knowledge'), description: t('privacyTech.guarantees.g2.desc', 'El servidor procesa sin ver contenido'), color: 'blue' },
              { icon: Key, title: t('privacyTech.guarantees.g3.title', 'Tu Clave'), description: t('privacyTech.guarantees.g3.desc', 'Solo tu puedes descifrar los resultados'), color: 'green' },
              { icon: Shield, title: t('privacyTech.guarantees.g4.title', 'Post-Quantum'), description: t('privacyTech.guarantees.g4.desc', 'Seguro contra computadoras cuanticas'), color: 'indigo' },
              { icon: FileText, title: t('privacyTech.guarantees.g5.title', 'Compliance'), description: t('privacyTech.guarantees.g5.desc', 'GDPR, HIPAA, SOC2 por diseno'), color: 'teal' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className={`w-14 h-14 bg-${item.color}-100 rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                  <item.icon className={`w-7 h-7 text-${item.color}-600`} />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeIn} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('privacyTech.comparison.title', 'FHE vs Metodos Tradicionales')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('privacyTech.comparison.subtitle', 'Por que FHE es superior para proteger datos sensibles durante el procesamiento.')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-xl overflow-hidden"
          >
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="py-4 px-4 text-left font-semibold text-gray-900">{t('privacyTech.comparison.feature', 'Caracteristica')}</th>
                  <th className="py-4 px-4 text-center font-semibold text-gray-900">
                    <div className="flex flex-col items-center">
                      <span>{t('privacyTech.comparison.traditional', 'ML Tradicional')}</span>
                      <span className="text-xs text-gray-500 font-normal">(TLS/Vault)</span>
                    </div>
                  </th>
                  <th className="py-4 px-4 text-center font-semibold text-purple-600">
                    <div className="flex flex-col items-center">
                      <span>Xcapit FHE</span>
                      <span className="text-xs text-purple-400 font-normal">(CKKS)</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <ComparisonRow
                  feature={t('privacyTech.comparison.rows.transit', 'Datos protegidos en transito')}
                  traditional={true}
                  fhe={true}
                />
                <ComparisonRow
                  feature={t('privacyTech.comparison.rows.rest', 'Datos protegidos en reposo')}
                  traditional={true}
                  fhe={true}
                />
                <ComparisonRow
                  feature={t('privacyTech.comparison.rows.processing', 'Datos protegidos DURANTE procesamiento')}
                  traditional={false}
                  fhe={true}
                />
                <ComparisonRow
                  feature={t('privacyTech.comparison.rows.serverAccess', 'Servidor NO puede ver datos')}
                  traditional={false}
                  fhe={true}
                />
                <ComparisonRow
                  feature={t('privacyTech.comparison.rows.breach', 'Inmune a breach del servidor')}
                  traditional={false}
                  fhe={true}
                />
                <ComparisonRow
                  feature={t('privacyTech.comparison.rows.insider', 'Protegido contra insiders maliciosos')}
                  traditional={false}
                  fhe={true}
                />
                <ComparisonRow
                  feature={t('privacyTech.comparison.rows.quantum', 'Resistente a ataques cuanticos')}
                  traditional={false}
                  fhe={true}
                />
              </tbody>
            </table>
          </motion.div>

          {/* Key Insight */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-8 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-8 text-white"
          >
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-yellow-300 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-bold mb-2">
                  {t('privacyTech.comparison.insight.title', 'El punto critico: procesamiento')}
                </h3>
                <p className="text-purple-100">
                  {t('privacyTech.comparison.insight.description', 'Los metodos tradicionales protegen datos en transito (TLS) y en reposo (encriptacion de disco). Pero cuando un servidor procesa los datos, DEBE descifrarlos primero. En ese momento, los datos estan expuestos en memoria y son vulnerables. FHE elimina este punto de exposicion completamente: los datos NUNCA se descifran en el servidor.')}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeIn} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('privacyTech.security.title', 'Seguridad Matematica')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('privacyTech.security.subtitle', 'La seguridad de FHE se basa en problemas matematicos que ni las computadoras cuanticas pueden resolver.')}
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* LWE Problem */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-slate-900 rounded-2xl p-8 text-white"
            >
              <div className="flex items-center gap-3 mb-6">
                <Cpu className="w-8 h-8 text-purple-400" />
                <h3 className="text-2xl font-bold">{t('privacyTech.security.lwe.title', 'Problema LWE')}</h3>
              </div>
              <p className="text-slate-300 mb-6">
                {t('privacyTech.security.lwe.description', 'FHE se basa en el problema "Learning With Errors" (LWE), considerado intratable incluso para computadoras cuanticas:')}
              </p>
              <div className="bg-slate-800 rounded-xl p-4 font-mono text-sm mb-6">
                <p className="text-green-400">A · s + e = b (mod q)</p>
                <p className="text-slate-500 mt-2">{t('privacyTech.security.lwe.formula', '// Encontrar s dado A y b es computacionalmente imposible')}</p>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-purple-400">128</div>
                  <div className="text-xs text-slate-400">bits seguridad</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-purple-400">2<sup>128</sup></div>
                  <div className="text-xs text-slate-400">operaciones para romper</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-purple-400">10<sup>20</sup>x</div>
                  <div className="text-xs text-slate-400">edad del universo</div>
                </div>
              </div>
            </motion.div>

            {/* Security Levels */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h3 className="text-2xl font-bold text-gray-900">
                {t('privacyTech.security.levels.title', 'Niveles de Seguridad')}
              </h3>

              {[
                { level: 'TC128', bits: '128', equiv: 'AES-128, RSA-3072', color: 'green', desc: t('privacyTech.security.levels.tc128', 'Estandar para datos comerciales') },
                { level: 'TC192', bits: '192', equiv: 'AES-192, RSA-7680', color: 'blue', desc: t('privacyTech.security.levels.tc192', 'Datos gubernamentales sensibles') },
                { level: 'TC256', bits: '256', equiv: 'AES-256, RSA-15360', color: 'purple', desc: t('privacyTech.security.levels.tc256', 'Maxima seguridad, post-quantum') },
              ].map((item, index) => (
                <div key={index} className={`bg-${item.color}-50 border border-${item.color}-200 rounded-xl p-6`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`font-bold text-${item.color}-700`}>{item.level}</span>
                    <span className={`text-sm text-${item.color}-600`}>{item.bits} bits</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{item.desc}</p>
                  <p className="text-xs text-gray-500">{t('privacyTech.security.levels.equivalent', 'Equivalente a:')} {item.equiv}</p>
                </div>
              ))}

              <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-xl p-6 border border-green-200">
                <div className="flex items-center gap-3 mb-2">
                  <Zap className="w-6 h-6 text-green-600" />
                  <span className="font-bold text-green-800">Xcapit {t('privacyTech.security.xcapitDefault', 'usa TC128 por defecto')}</span>
                </div>
                <p className="text-sm text-green-700">
                  {t('privacyTech.security.xcapitDefaultDesc', 'Configurable a 192 o 256 bits para casos de uso que requieran maxima seguridad.')}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeIn} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('privacyTech.faq.title', 'Preguntas Frecuentes')}
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-xl p-8"
          >
            {[
              {
                q: t('privacyTech.faq.q1.question', 'Si el servidor no ve mis datos, como puede hacer predicciones?'),
                a: t('privacyTech.faq.q1.answer', 'FHE permite operaciones matematicas (sumas, multiplicaciones) sobre datos encriptados. El modelo ML se ejecuta sobre los datos cifrados y produce un resultado cifrado. Tu descifras el resultado con tu clave privada. El servidor realiza todo el computo sin jamas ver un valor real.')
              },
              {
                q: t('privacyTech.faq.q2.question', 'Que pasa si hackean el servidor de Xcapit?'),
                a: t('privacyTech.faq.q2.answer', 'Incluso con acceso total al servidor, un atacante solo veria datos encriptados que son matematicamente indistinguibles de ruido aleatorio. Sin tu clave privada (que nunca sale de tu dispositivo), los datos son inutiles. Es como robar una caja fuerte sin la combinacion.')
              },
              {
                q: t('privacyTech.faq.q3.question', 'Es FHE lo mismo que encriptacion normal (AES/TLS)?'),
                a: t('privacyTech.faq.q3.answer', 'No. La encriptacion tradicional protege datos en transito y reposo, pero requiere descifrar para procesar. FHE es unica porque permite COMPUTAR sobre datos encriptados sin descifrarlos. Es una diferencia fundamental que elimina el punto de exposicion durante el procesamiento.')
              },
              {
                q: t('privacyTech.faq.q4.question', 'Por que no todos usan FHE si es tan seguro?'),
                a: t('privacyTech.faq.q4.answer', 'FHE fue inventado en 2009 y hasta hace pocos anos era demasiado lento para uso practico. Los avances recientes (CKKS 2017, TenSEAL 2020) lo han hecho viable para ML. Xcapit esta entre las primeras plataformas en ofrecer FHE productivo para empresas.')
              },
              {
                q: t('privacyTech.faq.q5.question', 'Cumple con GDPR, HIPAA y otras regulaciones?'),
                a: t('privacyTech.faq.q5.answer', 'Si. FHE excede los requisitos de estas regulaciones porque los datos personales NUNCA son accesibles por terceros. GDPR requiere proteccion de datos; con FHE, los datos estan protegidos incluso durante el procesamiento. Esto simplifica enormemente el compliance.')
              },
            ].map((item, index) => (
              <FAQItem
                key={index}
                question={item.q}
                answer={item.a}
                isOpen={openFAQ === index}
                onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
              />
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-purple-900 via-indigo-900 to-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t('privacyTech.cta.title', 'Protege tus datos con FHE')}
            </h2>
            <p className="text-xl text-purple-100 mb-8">
              {t('privacyTech.cta.subtitle', 'Agenda una demo para ver como Xcapit puede ayudar a tu organizacion a hacer ML sin comprometer la privacidad.')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://calendly.com/xcapit-demo"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-purple-900 px-8 py-4 rounded-xl font-semibold hover:bg-purple-50 transition-colors"
              >
                {t('privacyTech.cta.button', 'Agendar Demo')}
                <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href="/hub"
                className="inline-flex items-center gap-2 border border-white/30 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/10 transition-colors"
              >
                {t('privacyTech.cta.buttonSecondary', 'Ver Casos de Uso')}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-purple-400" />
              <span className="text-white font-bold text-xl">Xcapit Privacy</span>
            </div>
            <div className="flex items-center gap-6">
              <a href="/" className="text-slate-400 hover:text-white transition-colors">
                {t('footer.home', 'Inicio')}
              </a>
              <a href="/hub" className="text-slate-400 hover:text-white transition-colors">
                {t('footer.industries', 'Industrias')}
              </a>
              <a href="https://apifhe.xcapit.com/api/v2/docs/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors">
                {t('footer.apiDocs', 'API Docs')}
              </a>
            </div>
            <p className="text-slate-500 text-sm">
              © 2026 Xcapit. {t('footer.rights', 'Todos los derechos reservados.')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}
